import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, Subject } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, map, startWith, tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { SendbirdChatService } from 'src/app/layouts/sendbird-chat/sendbird-chat.service';
import { MiniGameService } from 'src/app/pages/minigame/minigame.service';
import { ScrollbarComponent } from 'src/app/shared/components/scrollbar/scrollbar.component';
import { HeaderMenu, HomeLabelSceneData } from 'src/app/shared/interfaces/game.interface';
import { LayoutService } from 'src/app/shared/service/layout.service';
import { LocalStorageService } from 'src/app/shared/service/localstorage.service';
import { HeaderLevel2MenuComponent } from '../header-level2-menu/header-level2-menu.component';

const LEFT_MENU_WIDTH = {
  WEB_CLOSE: '64px',
  WEB_OPEN: '240px',
  H5_CLOSE: '100%',
  H5_OPEN: '100%',
  hide: '0px',
};

const LEFT_MENU_TOP = {
  WEB_CLOSE: '0',
  WEB_OPEN: '0',
  H5_CLOSE: 'calc(100% + 56px)',
  H5_OPEN: '0',
};

@UntilDestroy()
@Component({
  selector: 'app-basic-layout',
  templateUrl: './basic-layout.component.html',
  styleUrls: ['./basic-layout.component.scss'],
  host: { class: 'flex' },
})
export class BasicLayoutComponent implements OnInit {
  constructor(
    public layout: LayoutService,
    private localStorageService: LocalStorageService,
    private appService: AppService,
    private deviceService: DeviceDetectorService,
    private miniGameService: MiniGameService,
    private sendbirdChatService: SendbirdChatService,
  ) {}

  leftMenuMode$: Subject<'side' | 'over'> = new Subject<'side' | 'over'>();
  leftMenuState: boolean = false;
  leftMenuBackdrop: boolean = false;
  leftMenuTop!: string;
  leftMenuWidth!: string;
  layoutContentMarginLeft!: string;

  fullMode!: boolean;
  conciseMode!: boolean;

  isH5!: boolean;

  /** 是否隐藏头部 */
  hideHead: boolean = false;

  /** 上次滑动时间 */
  prevTime: number = 0;
  /** 上一次滑动位置 */
  prevPos: number = 0;

  @ViewChild('scrollbar') scrollbar!: ScrollbarComponent;
  @ViewChild('mobileContent') mobileContent?: ElementRef<HTMLDivElement>;
  @ViewChild('level2Menu') level2Menu!: HeaderLevel2MenuComponent;
  private mobileContentLocked = false;
  private mobileContentScrollTop = 0;
  private mobileContentPrevStyles: Partial<CSSStyleDeclaration> | null = null;

  isDesktop: boolean = this.deviceService.isDesktop();
  /** 是否有二级菜单 */
  hideLevel2Head: boolean = false;
  /** layout-widgets是否浮在上面 */
  isFloat = false;
  themeClass = '';
  readonly bottomMenuVisible$ = this.layout.h5BottomMenuVisible$;
  isOpenSendbirdChat: boolean = false;
  lastPage: any;
  ngOnInit() {
    this.themeClass = this.appService.tenantConfig$.value?.config.themeClass || '';
    //左侧菜单状态判断
    combineLatest([
      this.layout.isH5$,
      this.layout.leftMenuState$,
      this.leftMenuMode$.pipe(startWith(this.getLeftMenuMode(document.body.offsetWidth))),
      this.layout.leftMenuInvisible$.pipe(startWith(undefined)),
    ])
      .pipe(untilDestroyed(this), debounceTime(0)) // RuntimeError: NG0100
      .subscribe(([isH5, state, mode, invisible]) => {
        if (!this.isDesktop && isH5 && state) {
          document.body.classList.add('hidden-y');
        } else {
          document.body.classList.remove('hidden-y');
        }
        this.leftMenuState = state;
        this.isH5 = isH5;
        if (!isH5 && this.isDesktop) this.hideHead = false;
        if (isH5) this.layout.lockLayoutScroll$.next(state);
        if (invisible) {
          this.leftMenuWidth = this.layoutContentMarginLeft = LEFT_MENU_WIDTH['hide'];
          this.leftMenuBackdrop = false;
        } else {
          switch (isH5) {
            case true:
              this.leftMenuTop = state ? LEFT_MENU_TOP['H5_OPEN'] : LEFT_MENU_TOP['H5_CLOSE'];
              this.leftMenuWidth = state ? LEFT_MENU_WIDTH['H5_OPEN'] : LEFT_MENU_WIDTH['H5_CLOSE'];
              this.layoutContentMarginLeft = '0';
              break;

            default:
              this.leftMenuTop = state ? LEFT_MENU_TOP['WEB_OPEN'] : LEFT_MENU_TOP['WEB_CLOSE'];
              this.leftMenuWidth = state ? LEFT_MENU_WIDTH['WEB_OPEN'] : LEFT_MENU_WIDTH['WEB_CLOSE'];
              this.layoutContentMarginLeft = mode === 'side' ? this.leftMenuWidth : LEFT_MENU_WIDTH['WEB_CLOSE'];
              break;
          }

          this.leftMenuBackdrop = state && mode === 'over';
          this.localStorageService.setValueByJsonStringify('platform-left-menu-state', state);
        }
        this.layout.leftMenuBackdrop$.next(this.leftMenuBackdrop);
      });

    //对会触发动画效果的订阅进行监听，模拟动画开始和完成
    [this.layout.leftMenuState$, this.layout.leftMenuInvisible$].forEach(_$ => {
      _$.pipe(
        distinctUntilChanged(),
        tap(() => this.layout.leftMenuTransition$.next('start')),
        delay(400), // 需与动画时间一致
        tap(() => this.layout.leftMenuTransition$.next('end')),
        untilDestroyed(this),
      ).subscribe();
    });

    //页面宽度变动，设置左侧菜单模式
    this.layout.resize$.pipe(debounceTime(100), untilDestroyed(this)).subscribe(v => {
      const mode = this.getLeftMenuMode(v[0]);
      this.leftMenuMode$.next(mode);
    });
    //每次页面改变置顶，并判断当前页面，设置简洁模式等
    this.layout.page$.pipe(untilDestroyed(this)).subscribe(page => {
      this.isFloat = page === 'home';
      if (this.lastPage !== page) {
        if (this.isH5 && !this.isDesktop) {
          this.resetMobileScrollPosition();
          this.hideHead = false;
        } else {
          this.scrollbar?.scrollToTop();
        }
      }
      this.lastPage = page;

      //设置无footer
      this.fullMode = false;
      this.layout.getComponentTree().forEach(x => {
        if (x._displayMode) {
          this.fullMode = {
            FullMode: true,
            normal: false,
          }[x._displayMode as 'FullMode' | 'normal'];
        }
      });

      if (['register', 'login', 'password'].includes(page)) {
        this.conciseMode = true;
        this.layout.leftMenuInvisible$.next(true);
      } else if (this.conciseMode) {
        this.conciseMode = false;
        this.layout.leftMenuInvisible$.next(false);
      }
    });

    const platformLeftMenuState = this.localStorageService.getValueByJsonParse('platform-left-menu-state', true);
    const desktopLeftMenuState = document.body.offsetWidth > 1200 ? platformLeftMenuState : false;
    this.layout.leftMenuState$.next(this.layout.isH5$.value ? false : desktopLeftMenuState); // h5时候页面变化恒定关闭菜单

    this.layout.scrollTop$.pipe(untilDestroyed(this)).subscribe(v => {
      if (v) this.onScroll(v);
    });

    /** h5使用原生时隐藏滚动 */
    this.layout.lockLayoutScroll$.pipe(untilDestroyed(this)).subscribe(x => {
      if (x && this.deviceService.isMobile()) {
        document.body.classList.add('hidden-y');
        this.lockMobileContentScroll();
      } else {
        document.body.classList.remove('hidden-y');
        this.unlockMobileContentScroll();
      }
    });

    //
    this.miniGameService.homeScenesSub$
      .pipe(
        untilDestroyed(this),
        map((x: HomeLabelSceneData) => {
          return x?.navigationMenus || [];
        }),
      )
      .subscribe((data: HeaderMenu[]) => {
        this.hideLevel2Head = data.length > 0 ? true : false;
      });

    this.sendbirdChatService.displayChat$.pipe(untilDestroyed(this)).subscribe(x => {
      this.isOpenSendbirdChat = x;
    });
  }

  getLeftMenuMode(documentWidth: number) {
    return documentWidth >= 1200 ? 'side' : 'over';
  }

  onScrollDistance(event: number) {
    this.layout.scrollTop$.next(event);
  }

  @HostListener('window:scroll')
  layoutScorll() {
    if (this.isH5) {
      const top = (document.querySelector('html') as HTMLHtmlElement).scrollTop;
      this.layout.scrollTop$.next(top);
    }
  }

  onScroll(currentPos: number) {
    if (this.isH5 && !this.isDesktop) {
      const currentTime = Date.now();
      const timeDiff = currentTime - this.prevTime;
      const scrollDiff = currentPos - this.prevPos;
      const speed = Math.ceil(Math.abs(scrollDiff / timeDiff));
      this.prevTime = currentTime;
      this.prevPos = currentPos;
      const fixedSpeed = 1;
      const offsetTop = 100;

      if (scrollDiff < 0) {
        // 向上滑动
        if (currentPos < offsetTop || speed > fixedSpeed) {
          this.hideHead = false;
        }
      } else if (scrollDiff > 0) {
        //向下滑动只关闭二级菜单，不再隐藏头部
        if (speed > fixedSpeed && !this.appService.isNativeApp) {
          this.level2Menu?.closePop();
        }
      }
    }
  }

  onScrollOldIos($event: any) {
    this.onScroll($event.target.scrollTop);
  }

  private resetMobileScrollPosition() {
    const docElement = document.documentElement;
    const body = document.body;
    if (docElement) docElement.scrollTop = 0;
    if (body) body.scrollTop = 0;
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        if (docElement) docElement.scrollTop = 0;
        if (body) body.scrollTop = 0;
      });
    }
  }

  private lockMobileContentScroll() {
    const content = this.mobileContent?.nativeElement;
    if (!content || this.mobileContentLocked) return;

    const rect = content.getBoundingClientRect();
    this.mobileContentScrollTop = content.scrollTop;
    this.mobileContentPrevStyles = {
      position: content.style.position,
      top: content.style.top,
      left: content.style.left,
      width: content.style.width,
      height: content.style.height,
      overflow: content.style.overflow,
      touchAction: content.style.touchAction,
    };

    content.style.position = 'fixed';
    content.style.top = `${rect.top}px`;
    content.style.left = `${rect.left}px`;
    content.style.width = `${rect.width}px`;
    content.style.height = `${rect.height}px`;
    content.style.overflow = 'hidden';
    content.style.touchAction = 'none';
    content.scrollTop = this.mobileContentScrollTop;
    this.mobileContentLocked = true;
  }

  private unlockMobileContentScroll() {
    const content = this.mobileContent?.nativeElement;
    if (!content || !this.mobileContentLocked) return;

    const prev = this.mobileContentPrevStyles;
    if (prev) {
      content.style.position = prev.position || '';
      content.style.top = prev.top || '';
      content.style.left = prev.left || '';
      content.style.width = prev.width || '';
      content.style.height = prev.height || '';
      content.style.overflow = prev.overflow || '';
      content.style.touchAction = prev.touchAction || '';
    } else {
      content.style.position = '';
      content.style.top = '';
      content.style.left = '';
      content.style.width = '';
      content.style.height = '';
      content.style.overflow = '';
      content.style.touchAction = '';
    }

    content.scrollTop = this.mobileContentScrollTop;
    this.mobileContentPrevStyles = null;
    this.mobileContentLocked = false;
  }
}
